import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MarketSearchConfig,
  PublisherConfigurationResponse,
  PublisherDirectSalesPricing,
  PublisherFeatures,
  PublisherStore,
  PublisherCampaignConfig,
  PublisherFrameListConfiguration,
} from 'components/common/types/Publisher.types';
import { Dsp } from 'components/common/types/Deal.types';
import { DEFAULT_START_DATE_OFFSET } from 'store/dealConfig/consts';
import { parseDefaultPricingModeByMediaType } from 'utils/resolvePublisherDefaultDirectSalesPricingMode';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import {
  convertPricingToJSONObject,
  convertReportConfigurationToJSONObject,
  convertToArrayOfMarketsForIndividualFeatureFlag,
  emptyDirectSalesPricing,
  convertFrameListConfigurationToJSONObject,
} from './utils';

const initialState = {
  configuration: {},
  campaignConfiguration: {},
  directSalesPricing: emptyDirectSalesPricing,
  dsps: {},
  publisherFeatures: {},
  startDateOffset: DEFAULT_START_DATE_OFFSET,
  reportConfiguration: {
    metricType: MetricType.FRAMES,
  },
  frameListConfiguration: {},
  marketSearchConfig: null,
  deliveredAllocationMetrics: false,
} as PublisherStore;

const publisherSlice = createSlice({
  name: 'publisherSlice',
  initialState,
  reducers: {
    changeConfiguration: (state, action: PayloadAction<PublisherConfigurationResponse>) => {
      const nextMarketId = action.payload.identifier;

      if (state.configuration.marketId && state.configuration.marketId !== nextMarketId) {
        state.campaignConfiguration = {};
        state.marketSearchConfig = null;
      }

      state.configuration = {
        marketId: nextMarketId,
        currencyCode: action.payload.currencyCode,
        flag: action.payload.applicationConfiguration.flag || '',
        iso2CountryCode: action.payload.iso2CountryCode,
        latitude: Number(action.payload.applicationConfiguration.latitude),
        localeCode: action.payload.applicationConfiguration.localeCode,
        longitude: Number(action.payload.applicationConfiguration.longitude),
        openStreetMapCountryName: action.payload.applicationConfiguration.countryName,
        timezoneOverride: action.payload.applicationConfiguration.timezoneOverride,
        timezone: action.payload.timeZone,
        type: action.payload.type,
        allocationReportDefaultRowGroups:
          action.payload.applicationConfiguration.allocationReportDefaultRowGroups?.replaceAll(' ', '').split(',') ||
          [],
        isAllocationReportSelectAllDefault:
          action.payload.applicationConfiguration.isAllocationReportSelectAllDefault === 'true',
        isAllocationReportSelectAllLocked:
          action.payload.applicationConfiguration.isAllocationReportSelectAllLocked === 'true',
        isAllocationReportSelectAllHidden:
          action.payload.applicationConfiguration.isAllocationReportSelectAllHidden === 'true',
      };
    },
    changeDsps: (state, action: PayloadAction<{ [mediaOwner: string]: Dsp }>) => {
      state.dsps = { ...action.payload };
    },
    changeFeatures: (state, action: PayloadAction<PublisherFeatures>) => {
      state.publisherFeatures = convertToArrayOfMarketsForIndividualFeatureFlag(action.payload);
    },
    changeDirectSalesPricing: (state, action: PayloadAction<PublisherDirectSalesPricing>) => {
      const { gridConfiguration, reportConfiguration, startDateOffset, frameListConfiguration } = action.payload;

      state.directSalesPricing = convertPricingToJSONObject(gridConfiguration);
      state.reportConfiguration = convertReportConfigurationToJSONObject(reportConfiguration);
      state.startDateOffset = startDateOffset ? Number(startDateOffset) : initialState.startDateOffset;
      state.frameListConfiguration = convertFrameListConfigurationToJSONObject(frameListConfiguration);
    },
    changeCampaignConfiguration: (state, action: PayloadAction<PublisherCampaignConfig>) => {
      const {
        requiredFields,
        greenTolerance,
        orangeTolerance,
        priority,
        defaultPricingModeByMediaType,
        deliveredAllocationMetrics,
      } = action.payload;

      state.deliveredAllocationMetrics = deliveredAllocationMetrics === 'true';

      state.campaignConfiguration = {};

      if (requiredFields) {
        state.directSalesRequiredFields = JSON.parse(requiredFields);
      }

      if (greenTolerance || orangeTolerance || priority) {
        state.targetTolerances = {
          greenTolerance: greenTolerance ?? '0.5',
          orangeTolerance: orangeTolerance ?? '0.10',
          priority: priority ?? '1',
        };
      }

      const parsedDefaultPricingModeByMediaType = parseDefaultPricingModeByMediaType(defaultPricingModeByMediaType);

      if (parsedDefaultPricingModeByMediaType) {
        state.campaignConfiguration.defaultPricingModeByMediaType = parsedDefaultPricingModeByMediaType;
      }
    },
    changeReportConfiguration: (state, action: PayloadAction<MetricType>) => {
      state.reportConfiguration.metricType = action.payload;
    },
    changeFrameListConfiguration: (state, action: PayloadAction<PublisherFrameListConfiguration>) => {
      state.frameListConfiguration = action.payload;
    },
    changeMarketSearchConfig: (state, action: PayloadAction<MarketSearchConfig>) => {
      state.marketSearchConfig = action.payload;
    },
    clearPublisherData: () => {
      return {
        ...initialState,
      };
    },
  },
});

export const {
  changeConfiguration,
  changeDsps,
  changeFeatures,
  changeDirectSalesPricing,
  clearPublisherData,
  changeCampaignConfiguration,
  changeReportConfiguration,
  changeFrameListConfiguration,
  changeMarketSearchConfig,
} = publisherSlice.actions;

export default publisherSlice.reducer;
